import { c as _c } from "react/compiler-runtime";
import { LoadingSpinner } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrowserViewportSize } from '../../hooks/use-browser-pane';
import type { SlotRect } from '../../hooks/use-slot-rect';
import { EASE_OUT, FRAME_SECONDS } from './browser-motion';
type BrowserLiveViewProps = {
  liveViewUrl: string | null;
  liveViewFailed: boolean;
  /** The size the remote page has now. The live view fits itself to the page once, when it
   *  connects, so a change here reloads the frame. */
  remoteViewport: BrowserViewportSize;
  /** A new size is on its way to the remote; what the frame draws meanwhile is stale. */
  resizing: boolean;
  /** Relative to the chat root; a change animates the frame there. */
  rect: SlotRect;
  /** `contain` shows the whole page (the pane); `cover` fills the box with its top (the card). */
  fit: 'contain' | 'cover';
  interactive: boolean;
  /** How long after `load` the frame is shown; the default covers the live view's first draw. */
  settleMs?: number;
};

// The live view reports `load` when its own shell is up; the first page frame follows up to a
// second and a half later, and the shell is blank white until then.
const SETTLE_MS = 1500;
const BrowserLiveView = t0 => {
  const $ = _c(38);
  const {
    liveViewUrl,
    liveViewFailed,
    remoteViewport,
    resizing,
    rect,
    fit,
    interactive,
    settleMs: t1
  } = t0;
  const settleMs = t1 === undefined ? SETTLE_MS : t1;
  const {
    t
  } = useTranslation("copilot");
  const reducedMotion = useReducedMotion();
  const [settledKey, setSettledKey] = useState(null);
  const settleTimer = useRef(null);
  const frameKey = liveViewUrl === null ? null : `${liveViewUrl}|${remoteViewport.width}x${remoteViewport.height}`;
  const ready = frameKey !== null && settledKey === frameKey && !resizing;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => clearTimeout(settleTimer.current ?? undefined);
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== frameKey || $[3] !== settleMs) {
    t4 = () => {
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
      }
      settleTimer.current = setTimeout(() => setSettledKey(frameKey), settleMs);
    };
    $[2] = frameKey;
    $[3] = settleMs;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleLoad = t4;
  const {
    width: vw,
    height: vh
  } = remoteViewport;
  const scale = fit === "cover" ? Math.max(rect.width / vw, rect.height / vh) : Math.min(rect.width / vw, rect.height / vh, 1);
  const left = (rect.width - vw * scale) / 2;
  const top = fit === "cover" ? 0 : (rect.height - vh * scale) / 2;
  const t5 = fit === "cover";
  const t6 = !interactive;
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = cx("absolute left-0 top-0 z-10 overflow-hidden bg-secondary", {
      "rounded-lg": t5,
      "pointer-events-none": t6
    });
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== rect.height || $[9] !== rect.left || $[10] !== rect.top || $[11] !== rect.width) {
    t8 = {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height
    };
    $[8] = rect.height;
    $[9] = rect.left;
    $[10] = rect.top;
    $[11] = rect.width;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = reducedMotion ? 0 : FRAME_SECONDS;
  let t10;
  if ($[13] !== t9) {
    t10 = {
      duration: t9,
      ease: EASE_OUT
    };
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== fit || $[16] !== liveViewFailed || $[17] !== liveViewUrl || $[18] !== ready) {
    t11 = !ready && <Placeholder compact={fit === "cover"} failed={liveViewFailed} loading={liveViewUrl !== null} />;
    $[15] = fit;
    $[16] = liveViewFailed;
    $[17] = liveViewUrl;
    $[18] = ready;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== frameKey || $[21] !== handleLoad || $[22] !== interactive || $[23] !== left || $[24] !== liveViewUrl || $[25] !== ready || $[26] !== scale || $[27] !== t || $[28] !== top || $[29] !== vh || $[30] !== vw) {
    t12 = liveViewUrl !== null && <div className="absolute origin-top-left transition-opacity duration-150 ease-out motion-reduce:transition-none" style={{
      left,
      top,
      width: vw,
      height: vh,
      transform: `scale(${scale})`,
      opacity: ready ? 1 : 0
    }}><iframe key={frameKey} src={withoutNavbar(liveViewUrl)} title={t(_temp)} inert={!interactive} onLoad={handleLoad} className={cx("absolute inset-0 w-full h-full border-0", !interactive && "pointer-events-none")} sandbox="allow-scripts allow-same-origin allow-forms" allow="clipboard-read; clipboard-write" referrerPolicy="no-referrer" /></div>;
    $[20] = frameKey;
    $[21] = handleLoad;
    $[22] = interactive;
    $[23] = left;
    $[24] = liveViewUrl;
    $[25] = ready;
    $[26] = scale;
    $[27] = t;
    $[28] = top;
    $[29] = vh;
    $[30] = vw;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t7 || $[36] !== t8) {
    t13 = <motion.div className={t7} data-dd-privacy="hidden" initial={false} animate={t8} transition={t10}>{t11}{t12}</motion.div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t7;
    $[36] = t8;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};
type PlaceholderProps = {
  compact: boolean;
  failed: boolean;
  loading: boolean;
};
const Placeholder = t0 => {
  const $ = _c(15);
  const {
    compact,
    failed,
    loading
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  let t1;
  if ($[0] !== failed || $[1] !== loading || $[2] !== t) {
    const getLabel = () => {
      if (failed) {
        return t(_temp2);
      }
      if (loading) {
        return t(_temp3);
      }
      return t(_temp4);
    };
    t1 = getLabel();
    $[0] = failed;
    $[1] = loading;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const label = t1;
  let t2;
  if ($[4] !== compact || $[5] !== failed) {
    t2 = !failed && <LoadingSpinner size={compact ? "compact" : "large"} color="tertiary" />;
    $[4] = compact;
    $[5] = failed;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = compact ? "text-caption-2" : "text-body-3";
  let t4;
  if ($[7] !== t3) {
    t4 = cx("text-tertiary", t3);
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== label || $[10] !== t4) {
    t5 = <span className={t4}>{label}</span>;
    $[9] = label;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">{t2}{t5}</div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};

// Browserbase draws its own address bar over the page unless asked not to.
const withoutNavbar = (liveViewUrl: string): string => {
  if (!liveViewUrl.startsWith('http')) return liveViewUrl;
  const url = new URL(liveViewUrl);
  url.searchParams.set('navbar', 'false');
  return url.toString();
};
export default BrowserLiveView;
function _temp($) {
  return $.chat.resources.browserSession.label;
}
function _temp2($) {
  return $.chat.browser.liveViewFailed;
}
function _temp3($_0) {
  return $_0.chat.browser.loadingLiveView;
}
function _temp4($_1) {
  return $_1.chat.browser.starting;
}